import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { Context } from '@grandvisionhq/carts-v2';
import { useLocation, useLink } from '@grandvisionhq/frk-routing';
import { Hooks } from '@grandvisionhq/fun-orders';
import { PaymentFlow, PaymentResult } from '@grandvisionhq/payments';
import { CreateOrderErrors, CheckoutErrorsMapping } from '../../mappers/errors-mapping.js';
import { CreateOrderError } from '../../models/orders/create-order-error.js';
import '../../models/cart/cart-state.js';
import { CheckoutStepErrorType } from '../../models/checkout/checkout-step-error-type.js';
import '../../models/checkout-settings.js';
import '../../models/step-shipping/shipping-methods.js';
import '../../models/shared.js';
import 'lodash/isEmpty';
import '@grandvisionhq/utils';
import '../../utils/get-customer-details-payload.js';
import { appendQueryParamToUrl } from '../../utils/get-updated-url-with-query-params.js';
import 'history';
import '../../utils/has-cart-errors.js';
import { dispatchCartChangeEvent } from '../carts/use-cart-change-listener.js';

const useCreateOrder = ({ onError , returnPath  } = {})=>{
    const [paymentFlow, setPaymentFlow] = React.useState(PaymentFlow.UNDEFINED);
    const [orderResponse, setOrderResponse] = React.useState(undefined);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const { cart  } = Context.useCart();
    const { setContext  } = Context.useCartSettings();
    const { replace  } = useLocation();
    const { href: paymentHref  } = useLink({
        route: 'checkout.payment',
        params: {
            cartId: ''
        }
    }) || {};
    const { placeOrder  } = Hooks.usePlaceOrder();
    /**
   * @description  call createOrder endpoint with payload and handle response with redirection
   * @argument orderPayload request object of type CreateOrderRequest
   */ const createOrder = React.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(orderPayload) {
            setLoading(true);
            setOrderResponse(undefined);
            const paymentMethod = orderPayload.paymentRequest.paymentMethod;
            try {
                if (!paymentMethod) {
                    console.error('Need a payment method to create an order');
                    throw new CreateOrderError({
                        status: CreateOrderErrors.ORDER_CREATION_CALL_FAILED
                    });
                }
                if (!cart) {
                    throw new CreateOrderError({
                        status: CreateOrderErrors.ORDER_CREATION_CALL_FAILED,
                        label: CheckoutErrorsMapping.CartNotFound
                    });
                }
                setError(null);
                const placeOrderResponse = yield placeOrder(orderPayload);
                setOrderResponse(placeOrderResponse);
                if (placeOrderResponse && placeOrderResponse.redirect) {
                    if (placeOrderResponse.httpMethod !== 'GET') {
                        throw new CreateOrderError({
                            status: CreateOrderErrors.UNSUPPORTED_REDIRECT_METHOD
                        });
                    }
                    dispatchCartChangeEvent(orderPayload.checkoutId);
                    (placeOrderResponse === null || placeOrderResponse === void 0 ? void 0 : placeOrderResponse.redirectUrl) && replace(placeOrderResponse === null || placeOrderResponse === void 0 ? void 0 : placeOrderResponse.redirectUrl);
                } else if (placeOrderResponse === null || placeOrderResponse === void 0 ? void 0 : placeOrderResponse.success) {
                    if (placeOrderResponse.resultType === 'QRCODE') {
                        setPaymentFlow(PaymentFlow.QR_CODE);
                    }
                    return swcHelpers.objectSpread({}, placeOrderResponse, {
                        cartId: orderPayload.checkoutId
                    });
                } else if (!(placeOrderResponse === null || placeOrderResponse === void 0 ? void 0 : placeOrderResponse.success) && (placeOrderResponse === null || placeOrderResponse === void 0 ? void 0 : placeOrderResponse.productPriceUpdated)) {
                    throw new CreateOrderError({
                        status: CreateOrderErrors.PRODUCT_PRICE_UPDATED,
                        cartHash: placeOrderResponse.cartHash,
                        cartId: placeOrderResponse.cartId,
                        type: orderPayload.paymentRequest.paymentMethod
                    });
                } else if (!(placeOrderResponse === null || placeOrderResponse === void 0 ? void 0 : placeOrderResponse.success) && (placeOrderResponse === null || placeOrderResponse === void 0 ? void 0 : placeOrderResponse.cartId)) {
                    throw new CreateOrderError({
                        status: CreateOrderErrors.ORDER_CREATION_CALL_FAILED,
                        label: CheckoutErrorsMapping.SomethingWentWrong,
                        cartHash: placeOrderResponse.cartHash,
                        cartId: placeOrderResponse.cartId,
                        type: orderPayload.paymentRequest.paymentMethod
                    });
                } else {
                    throw new CreateOrderError({
                        status: CreateOrderErrors.ORDER_CREATION_CALL_FAILED,
                        label: CheckoutErrorsMapping.SomethingWentWrong,
                        cartId: orderPayload.checkoutId,
                        type: orderPayload.paymentRequest.paymentMethod
                    });
                }
                setLoading(false);
                return placeOrderResponse;
            } catch (e) {
                console.error(`Order creation failed for ${orderPayload.checkoutId}`);
                setLoading(false);
                let isProductPriceUpdatedError = false;
                if (e instanceof CreateOrderError) {
                    onError && onError(e.status, e === null || e === void 0 ? void 0 : e.label);
                    if ((e === null || e === void 0 ? void 0 : e.cartHash) && (e === null || e === void 0 ? void 0 : e.cartId)) {
                        setContext && setContext({
                            cartId: e === null || e === void 0 ? void 0 : e.cartId,
                            cartHash: e === null || e === void 0 ? void 0 : e.cartHash
                        });
                    }
                    const returnUrl = returnPath !== null && returnPath !== void 0 ? returnPath : paymentHref;
                    if (returnUrl) {
                        var ref;
                        isProductPriceUpdatedError = (e === null || e === void 0 ? void 0 : e.status) === CreateOrderErrors.PRODUCT_PRICE_UPDATED;
                        const status = isProductPriceUpdatedError ? CheckoutStepErrorType.ProductPriceUpdated : PaymentResult.Failed;
                        replace(appendQueryParamToUrl(returnUrl, {
                            cartHash: e === null || e === void 0 ? void 0 : e.cartHash,
                            cartId: e === null || e === void 0 ? void 0 : e.cartId,
                            type: e === null || e === void 0 ? void 0 : (ref = e.type) === null || ref === void 0 ? void 0 : ref.toUpperCase(),
                            status
                        }));
                    }
                }
                isProductPriceUpdatedError ? setError(CreateOrderErrors.PRODUCT_PRICE_UPDATED) : setError(CreateOrderErrors.ORDER_CREATION_CALL_FAILED);
                return {
                    success: false,
                    redirect: false
                };
            }
        });
        return function(orderPayload) {
            return _ref.apply(this, arguments);
        };
    }(), [
        cart,
        onError,
        paymentHref,
        placeOrder,
        replace,
        returnPath,
        setContext
    ]);
    return React.useMemo(()=>[
            createOrder,
            {
                loading,
                error,
                orderResponse,
                paymentFlow,
                setPaymentFlow
            }, 
        ]
    , [
        createOrder,
        loading,
        error,
        orderResponse,
        paymentFlow,
        setPaymentFlow
    ]);
};

export { useCreateOrder };
//# sourceMappingURL=use-create-order.js.map
